/**
 * Agent-wallet session state (#688). Must sit inside EnvProvider, WalletProvider and GhostProvider.
 *
 *   - Loads the stored session for (env, connected address); re-checks it with /info extraAgents on load and when
 *     the tab regains focus, and forgets it if Hyperliquid no longer lists it (e.g. replaced from another device).
 *   - Forgets it at expiry (timer), on disable, and when the wallet switches to a different address (the previous
 *     address's sessions are deleted on both envs).
 *   - Ghost mode: unavailable (read-only), the stored session is kept for when you exit ghost mode.
 *   - Mainnet: unavailable while mainnet trading is off (same gate as orders).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Hex } from 'viem';
import { useEnv } from '../config/env';
import { signL1WithAgent, WalletChainUnknownError } from '../hl/exchange';
import { mainnetTradingEnabled, type AgentSigner } from '../ticket/order';
import { useViewedAccount, useWallet } from '../wallet/context';
import { useGeo } from '../geo/context';
import { isUserRejection, walletErrorMessage } from '../wallet/errors';
import { AgentApproveError, AgentNotRegisteredError, checkAgent, enableAgent } from './agent';
import { AgentContext, type AgentContextValue, type AgentUnavailableReason } from './context';
import { t } from '../i18n/i18n';
import { clearAgentSession, clearAgentSessionsFor, loadAgentSession, saveAgentSession, type AgentSession } from './storage';

export interface AgentProviderProps {
  children: ReactNode;
  /** Tests: injectable fetch / clock / verify timing. */
  fetchImpl?: typeof fetch;
  now?: () => number;
  verify?: { tries: number; delayMs: number };
}

/** setTimeout's max delay (~24.8 days); longer timers are re-armed. */
const MAX_TIMER_MS = 2 ** 31 - 1;

export function AgentProvider({ children, fetchImpl, now = Date.now, verify }: AgentProviderProps) {
  const { env } = useEnv();
  const wallet = useWallet();
  const viewed = useViewedAccount();
  const user = wallet.status === 'connected' && wallet.address ? wallet.address.toLowerCase() : null;

  const [session, setSession] = useState<AgentSession | null>(null);
  const [enabling, setEnabling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const nowRef = useRef(now);
  nowRef.current = now;
  const fetchRef = useRef(fetchImpl);
  fetchRef.current = fetchImpl;

  // Address change: forget the previous address's agents (both envs). A reload (null → A) keeps them.
  const prevUser = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevUser.current;
    if (user) {
      if (prev && prev !== user) clearAgentSessionsFor(prev);
      prevUser.current = user;
    }
  }, [user]);

  // Load the stored session for (env, user) and re-check it with Hyperliquid.
  const [checkTick, setCheckTick] = useState(0);
  useEffect(() => {
    if (!user) {
      setSession(null);
      return;
    }
    const s = loadAgentSession(env.name, user, nowRef.current());
    setSession(s);
    if (!s) return;
    let cancelled = false;
    void checkAgent(env, user, s.agentAddress, { fetchImpl: fetchRef.current, now: nowRef.current() }).then((st) => {
      if (cancelled || st !== 'missing') return;
      clearAgentSession(env.name, user);
      setSession((cur) => (cur && cur.agentAddress === s.agentAddress ? null : cur));
      setNotice(t('agent.revoked'));
    });
    return () => {
      cancelled = true;
    };
  }, [env, user, checkTick]);

  // Re-check when the tab comes back (another device may have replaced the agent meanwhile).
  useEffect(() => {
    if (!session) return;
    const onVis = () => {
      if (document.visibilityState === 'visible') setCheckTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [session]);

  // Expiry timer.
  useEffect(() => {
    if (!session) return;
    let h: ReturnType<typeof setTimeout>;
    const arm = () => {
      const left = session.validUntil - nowRef.current();
      if (left <= 0) {
        clearAgentSession(session.env, session.user);
        setSession((cur) => (cur === session ? null : cur));
        setNotice(t('agent.expired'));
        return;
      }
      h = setTimeout(arm, Math.min(left, MAX_TIMER_MS));
    };
    arm();
    return () => clearTimeout(h);
  }, [session]);

  // #769: browse-only jurisdictions get no 1-click trading (no approveAgent, no agent signing).
  const geoCanTrade = useGeo().canTrade;
  let unavailableReason: AgentUnavailableReason | null = null;
  if (!geoCanTrade) unavailableReason = 'region';
  else if (viewed.mode === 'ghost') unavailableReason = 'ghost';
  else if (!user || !wallet.provider) unavailableReason = 'no-wallet';
  else if (env.isMainnet && !mainnetTradingEnabled()) unavailableReason = 'mainnet-off';

  const active = session && session.user === user && session.env === env.name ? session : null;

  const enable = useCallback(async (): Promise<boolean> => {
    if (unavailableReason || enabling || !wallet.provider || !wallet.address) return false;
    setError(null);
    setNotice(null);
    setEnabling(true);
    try {
      const s = await enableAgent({
        provider: wallet.provider,
        account: wallet.address as Hex,
        env,
        now: nowRef.current,
        fetchImpl: fetchRef.current,
        verify,
      });
      saveAgentSession(s);
      setSession(s);
      return true;
    } catch (e) {
      if (isUserRejection(e)) setError(t('trade.agent.canceled_in_wallet'));
      else if (e instanceof WalletChainUnknownError || e instanceof AgentNotRegisteredError) setError(e.message);
      else if (e instanceof AgentApproveError) setError(t('common.approval_refused', { error: e.message }));
      else setError(t('agent.enable_failed', { error: walletErrorMessage(e) }));
      return false;
    } finally {
      setEnabling(false);
    }
  }, [unavailableReason, enabling, wallet.provider, wallet.address, env, verify]);

  const disable = useCallback(() => {
    if (user) clearAgentSession(env.name, user);
    setSession(null);
    setError(null);
    setNotice(null);
  }, [env.name, user]);

  const forget = useCallback(
    (message: string) => {
      if (user) clearAgentSession(env.name, user);
      setSession(null);
      setNotice(t('agent.turned_off', { reason: message }));
    },
    [env.name, user],
  );

  const clearMessages = useCallback(() => {
    setError(null);
    setNotice(null);
  }, []);

  const signer = useMemo<AgentSigner | null>(() => {
    if (!active) return null;
    const key = active.agentKey;
    const source = { signingSource: env.signingSource };
    return {
      user: active.user,
      agentAddress: active.agentAddress,
      sign: (action, nonce) => signL1WithAgent(key, action, nonce, source),
    };
  }, [active, env.signingSource]);

  const value = useMemo<AgentContextValue>(() => {
    const status = unavailableReason ? 'unavailable' : enabling ? 'enabling' : active ? 'on' : 'off';
    return {
      status,
      unavailableReason,
      session: active ? { agentAddress: active.agentAddress, agentName: active.agentName, validUntil: active.validUntil } : null,
      signer: status === 'on' ? signer : null,
      error,
      notice,
      enable,
      disable,
      forget,
      clearMessages,
    };
  }, [unavailableReason, enabling, active, signer, error, notice, enable, disable, forget, clearMessages]);

  return <AgentContext.Provider value={value}>{children}</AgentContext.Provider>;
}
